import { ClerkProvider } from '@clerk/react-router';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AlbumIcon from '@mui/icons-material/LibraryMusic';
import type { Theme } from '@mui/material/styles';
import jsonRestProvider from 'ra-data-json-server';
import {
  Admin, CustomRoutes, Resource, defaultDarkTheme,
} from 'react-admin';
import type { RaThemeOptions } from 'react-admin';
import { BrowserRouter, Route } from 'react-router-dom';

import AlbumList from './Album/AlbumList';
import AlbumCreate from './Album/Create/AlbumCreate';
import AlbumEdit from './Album/Edit/AlbumEdit';
import AlbumEngineerList from './AlbumEngineer/AlbumEngineerList';
import AlbumEngineerOrder from './AlbumEngineerSorting/AlbumEngineerOrderPage';
import AuthCallback from './Auth/AuthCallback';
import { authHttpClient, authProvider } from './Auth/authProvider';
import LoginPage from './Auth/LoginPage';
import { env } from './env';
import industryEdit from './IndustryInsights/IndustryInsightsEdit';
import industryList from './IndustryInsights/IndustryInsightsList';
import { Layout } from './Layout';
import AccountMemberPermissionsList from './Permissions/AccountMemberPermissionsList';
import MemberPermissions from './Permissions/MemberPermissions';

export const adminTheme: RaThemeOptions = {
  ...defaultDarkTheme,
  palette: {
    ...defaultDarkTheme.palette,
    mode: 'dark' as const,
    primary: {
      ...defaultDarkTheme.palette?.primary,
      main: '#D2A969',
      dark: '#6E532A',
    },
    secondary: {
      main: '#D2A969',
    },
    background: {
      ...defaultDarkTheme.palette?.background,
      default: '#1B1D1F',
      paper: '#252729',
    },
  },
  typography: {
    ...defaultDarkTheme.typography,
    fontFamily: '"Montserrat", "Trebuchet MS", Helvetica, sans-serif',
  },
  components: {
    ...defaultDarkTheme.components,
    RaAppBar: {
      styleOverrides: {
        root: {
          '& .RaAppBar-toolbar': {
            backgroundColor: '#1B1D1F',
            color: '#FFFFFF',
            backgroundImage: 'none',
          },
        },
      },
    },
    RaMenuItemLink: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '& .RaMenuItemLink-icon': {
            color: theme.palette.secondary.main,
          },
        }),
      },
    },
  },
};

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <ClerkProvider
        publishableKey={env.REACT_APP_CLERK_PUBLISHABLE_KEY}
        afterSignOutUrl='/login'
        signInUrl='/login'
      >
        <CustomRoutes>
          <Route path='/login/*' element={<LoginPage />} />
          <Route path='/auth/callback' element={<AuthCallback />} />
        </CustomRoutes>
        <Admin
          dataProvider={jsonRestProvider('/api/admin/discography', authHttpClient)}
          layout={Layout}
          authProvider={authProvider}
          loginPage={LoginPage}
          theme={adminTheme}
          defaultTheme='dark'
        >
          <Resource
            name='albums'
            icon={AlbumIcon}
            list={AlbumList}
            edit={AlbumEdit}
            create={AlbumCreate}
          />
          <Resource
            name='industry-contributors'
            icon={AssessmentIcon}
            list={industryList}
            edit={industryEdit}
            options={{ label: 'Industry Insights' }}
          />
          <Resource name='engineers' list={AlbumEngineerList} />
          <CustomRoutes>
            <Route path='/engineer/order' element={<AlbumEngineerOrder />} />
            <Route path='/permissions' element={<AccountMemberPermissionsList />} />
            <Route path='/permissions/:contactIdentifier' element={<MemberPermissions />} />
          </CustomRoutes>

        </Admin>
      </ClerkProvider>
    </BrowserRouter>
  );
}
